import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';

// ── Mobile card (replaces the table below 768px — see
// SHARC_Mobile_Responsive_Spec_v1.1, §5.3) ──────────────────────────────────
function EventCardMobile({ event, formatDate }) {
    const to = `/events/${event.slug || event.id}`;
    return (
        <Link to={to} style={mobileStyles.card}>
            <div style={mobileStyles.cardTopRow}>
                <span style={mobileStyles.cardDate}>{formatDate(event.event_date)}</span>
                {event.est_attendance ? (
                    <span style={mobileStyles.cardAttendance}>{event.est_attendance} att.</span>
                ) : null}
            </div>
            <div style={mobileStyles.cardHeadliner}>{event.headliner || '—'}</div>
            {(event.venue_name || event.venue_city || event.venue_state) && (
                <div style={mobileStyles.cardVenue}>
                    {event.venue_name}
                    {(event.venue_city || event.venue_state) && (
                        <span style={mobileStyles.cardLocation}>
                            {event.venue_name ? ' · ' : ''}
                            {[event.venue_city, event.venue_state].filter(Boolean).join(', ')}
                        </span>
                    )}
                </div>
            )}
            {event.number_of_artists ? (
                <span style={mobileStyles.badge}>{event.number_of_artists} artists</span>
            ) : null}
        </Link>
    );
}

function EventList() {
    const isMobile = useIsMobile();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        axios.get(`${config.API_URL}/events`)
            .then(res => {
                setEvents(res.data);
                setLoading(false);
            })
            .catch(err => {
                setError('Failed to load events');
                setLoading(false);
            });
    }, []);

    if (loading) return <div style={styles.message}>Loading events...</div>;
    if (error) return <div style={styles.error}>{error}</div>;

    const filtered = events.filter(e =>
        e.headliner?.toLowerCase().includes(search.toLowerCase()) ||
        e.venue_name?.toLowerCase().includes(search.toLowerCase()) ||
        e.venue_city?.toLowerCase().includes(search.toLowerCase())
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
        });
    };

    return (
        <div>
            <div style={styles.pageHeader}>
                <h1 style={styles.pageTitle}>Events</h1>
                <span style={styles.count}>{filtered.length} events</span>
            </div>

            <input
                type="text"
                placeholder="Search by headliner, venue, or city..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={styles.searchInput}
            />

            {isMobile ? (
                <div style={mobileStyles.cardList}>
                    {filtered.map((event) => (
                        <EventCardMobile key={event.id} event={event} formatDate={formatDate} />
                    ))}
                    {filtered.length === 0 && (
                        <div style={styles.message}>No events found.</div>
                    )}
                </div>
            ) : (
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Date</th>
                                <th style={styles.th}>Headliner</th>
                                <th style={styles.th}>Venue</th>
                                <th style={styles.th}>Location</th>
                                <th style={styles.th}>Artists</th>
                                <th style={styles.th}>Attendance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((event, index) => (
                                <tr key={event.id} style={index % 2 === 0 ? styles.trEven : styles.trOdd}>
                                    <td style={styles.td}>
                                        <Link to={`/events/${event.slug || event.id}`} style={styles.dateLink}>
                                            {formatDate(event.event_date)}
                                        </Link>
                                    </td>
                                    <td style={styles.td}>
                                        <Link to={`/events/${event.slug || event.id}`} style={styles.tableLink}>
                                            {event.headliner || '—'}
                                        </Link>
                                    </td>
                                    <td style={styles.td}>{event.venue_name}</td>
                                    <td style={styles.td}>
                                        {[event.venue_city, event.venue_state].filter(Boolean).join(', ')}
                                    </td>
                                    <td style={styles.td}>
                                        <span style={styles.badge}>{event.number_of_artists}</span>
                                    </td>
                                    <td style={{...styles.td, color: '#aaa', fontSize: '13px'}}>
                                        {event.est_attendance || '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filtered.length === 0 && (
                        <div style={styles.message}>No events found.</div>
                    )}
                </div>
            )}
        </div>
    );
}

const styles = {
    pageHeader: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '16px',
        marginBottom: '24px',
    },
    pageTitle: {
        fontSize: '32px',
        fontWeight: 'bold',
        color: '#e0e0e0',
    },
    count: {
        fontSize: '16px',
        color: '#aaa',
    },
    searchInput: {
        width: '100%',
        padding: '10px 16px',
        backgroundColor: '#1e1e1e',
        border: '1px solid #333',
        borderRadius: '8px',
        color: '#e0e0e0',
        fontSize: '14px',
        marginBottom: '24px',
        boxSizing: 'border-box',
        outline: 'none',
    },
    tableContainer: {
        overflowX: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px',
    },
    th: {
        textAlign: 'left',
        padding: '10px 16px',
        color: '#aaa',
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: '1px solid #333',
    },
    td: {
        padding: '12px 16px',
        color: '#e0e0e0',
        borderBottom: '1px solid #222',
    },
    trEven: {
        backgroundColor: '#1e1e1e',
    },
    trOdd: {
        backgroundColor: '#1a1a1a',
    },
    tableLink: {
        color: '#bb86fc',
        textDecoration: 'none',
    },
    dateLink: {
        color: '#e0e0e0',
        textDecoration: 'none',
        fontSize: '13px',
    },
    badge: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '2px 8px',
        fontSize: '13px',
        color: '#bb86fc',
        fontWeight: 'bold',
    },
    message: {
        textAlign: 'center',
        color: '#aaa',
        fontSize: '16px',
        padding: '60px 0',
    },
    error: {
        textAlign: 'center',
        color: '#cf6679',
        fontSize: '16px',
        padding: '60px 0',
    },
};

// ── Mobile card styles (Spec: SHARC_Mobile_Responsive_Spec_v1.1, §5.3) ──────
// Kept as a separate object, only used when useIsMobile() is true, so the
// desktop table above is completely untouched.
const mobileStyles = {
    cardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    card: {
        display: 'block',
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        padding: '14px 16px',
        textDecoration: 'none',
        color: '#e0e0e0',
    },
    cardTopRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        marginBottom: '6px',
    },
    cardDate: {
        fontSize: '13px',
        color: '#aaa',
    },
    cardAttendance: {
        fontSize: '12px',
        color: '#aaa',
        whiteSpace: 'nowrap',
    },
    cardHeadliner: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#bb86fc',
        marginBottom: '4px',
    },
    cardVenue: {
        fontSize: '13px',
        color: '#ccc',
        marginBottom: '8px',
    },
    cardLocation: {
        color: '#aaa',
    },
    badge: {
        display: 'inline-block',
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '2px 8px',
        fontSize: '12px',
        color: '#bb86fc',
        fontWeight: 'bold',
    },
};

export default EventList;